import React, { useState, useMemo } from "react";
import "./MaintenanceView.css";
// Importing global grid styles
import "../management/PremiumGrid.css";
// Reuse drawer layout from management views where possible, or rely on .vm-form-container

type MaintenanceCategory = "사고 수리" | "일반 정비" | "정기 검사";
type MaintenanceStatus = "완료" | "진행중" | "예약됨";

interface MaintenanceRecord {
    id: string; // e.g. M-20231012-01
    date: string;
    vehicleNumber: string;
    vehicleModel: string;
    depot: string;
    driver: string; // "박상민"
    category: MaintenanceCategory;
    shop: string; // "블루핸즈 강남점"
    details: string; // "우측 범퍼 교환"
    cost: number;
    status: MaintenanceStatus;
    apiSynced: boolean;
    technicianNote?: string;
    nextServiceDate?: string;
    accumulatedMileage: number; // e.g. 45000
    faultLevel?: "운전자 100%" | "상대방 100%" | "자연 마모" | "쌍방 과실";
    laborCost?: number;
    partsCost?: number;
}

// Mock Data representing sync with MOLIT (국토교통부)
const MOCK_RECORDS: MaintenanceRecord[] = [
    {
        id: "M-20231012-01",
        date: "2023-10-12",
        vehicleNumber: "12가 1111",
        vehicleModel: "Kia Bongo III EV",
        depot: "서초",
        driver: "김기사",
        category: "사고 수리",
        shop: "기아 오토큐 서초점",
        details: "전면 후드 및 우측 휀더 판금 도장",
        cost: 850000,
        status: "완료",
        apiSynced: true,
        technicianNote: "보험 처리 완료. 내부 프레임 손상 없음.",
        nextServiceDate: "2024-04-12",
        accumulatedMileage: 32450,
        faultLevel: "운전자 100%",
        laborCost: 350000,
        partsCost: 500000
    },
    {
        id: "M-20231015-02",
        date: "2023-10-15",
        vehicleNumber: "34나 2222",
        vehicleModel: "Hyundai Porter II Electric",
        depot: "강남",
        driver: "박상민",
        category: "정기 검사",
        shop: "교통안전공단 강남검사소",
        details: "정기 종합 검사",
        cost: 54000,
        status: "완료",
        apiSynced: true,
        technicianNote: "제동력 우수. 다음 검사일까지 특이사항 없음.",
        accumulatedMileage: 89000
    },
    {
        id: "M-20231020-03",
        date: "2023-10-20",
        vehicleNumber: "56다 3333",
        vehicleModel: "Hyundai ST1",
        depot: "역삼",
        driver: "이승우",
        category: "일반 정비",
        shop: "블루핸즈 역삼점",
        details: "타이어 4짝 교체 (마모 한계 도달)",
        cost: 420000,
        status: "진행중",
        apiSynced: false,
        technicianNote: "재고 확인 중. 22일 입고 예정.",
        accumulatedMileage: 55000,
        faultLevel: "자연 마모"
    },
    {
        id: "M-20231022-04",
        date: "2023-10-22",
        vehicleNumber: "78라 4444",
        vehicleModel: "Kia Bongo III EV",
        depot: "양재",
        driver: "최동원",
        category: "일반 정비",
        shop: "기아 오토큐 양재점",
        details: "브레이크 패드 교체",
        cost: 120000,
        status: "예약됨",
        apiSynced: false,
        accumulatedMileage: 41200,
        faultLevel: "자연 마모"
    }
];

export default function MaintenanceHistoryView() {
    const [quickFilter, setQuickFilter] = useState<MaintenanceCategory | "all">("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedRecord, setSelectedRecord] = useState<MaintenanceRecord | null>(null);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [isManualAddOpen, setIsManualAddOpen] = useState(false);
    const [manualAddCategory, setManualAddCategory] = useState<MaintenanceCategory>("일반 정비");

    const filteredRecords = useMemo(() => {
        let rows = MOCK_RECORDS;
        if (quickFilter !== "all") {
            rows = rows.filter(r => r.category === quickFilter);
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            rows = rows.filter(
                r => r.vehicleNumber.toLowerCase().includes(q) || r.driver.toLowerCase().includes(q)
            );
        }
        return rows;
    }, [quickFilter, searchQuery]);

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedIds(filteredRecords.map(r => r.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id: string) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const totalCostThisMonth = MOCK_RECORDS.reduce((acc, curr) => acc + curr.cost, 0);
    const activeTickets = MOCK_RECORDS.filter(r => r.status === "진행중" || r.status === "예약됨").length;
    const syncedCount = MOCK_RECORDS.filter(r => r.apiSynced).length;

    // Advanced KPIs
    const estimatedDepreciation = MOCK_RECORDS.filter(r => r.category === "사고 수리").reduce((acc, curr) => acc + (curr.cost * 1.5), 0); // Mock logic for depreciation
    const avgDowntime = 4.5; // Mock average days

    const isDrawerOpen = Boolean(selectedRecord);

    const getCategoryBadgeClass = (category: string) => {
        if (category === "사고 수리") return "m-badge-accident";
        if (category === "일반 정비") return "m-badge-repair";
        if (category === "정기 검사") return "m-badge-inspection";
        return "grid-badge-neutral";
    };

    return (
        <div className="vm-page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                <div className="soh-header-left">
                    <h2 className="soh-header-title">정비 내역 조회</h2>
                    <div className="soh-header-sub">차량별 사고 및 일반 정비 이력을 조회하고, 수리 비용과 소요 기간을 분석합니다.</div>
                </div>
                <button 
                    className="vm-action-btn-compact primary" 
                    onClick={() => setIsManualAddOpen(true)}
                    style={{ padding: '10px 16px', borderRadius: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)' }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    수동 등록
                </button>
            </div>

            {/* KPI Section - History Focused */}
            <div className="maintenance-kpi-bar" style={{ gap: '12px' }}>
                <div className="m-kpi-card" style={{ flex: 1 }}>
                    <div className="m-kpi-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                            <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        조회 기간 내 정비 건수
                    </div>
                    <div className="m-kpi-value" style={{ color: '#1e3a8a' }}>
                        {filteredRecords.length} <span className="m-kpi-sub" style={{ color: '#60a5fa' }}>건</span>
                    </div>
                </div>
                <div className="m-kpi-card" style={{ flex: 1 }}>
                    <div className="m-kpi-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="1" x2="12" y2="23"></line>
                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                        </svg>
                        총 정비 발생 비용
                    </div>
                    <div className="m-kpi-value" style={{ color: '#be123c' }}>
                        {totalCostThisMonth.toLocaleString()} <span className="m-kpi-sub" style={{ color: '#fda4af' }}>원</span>
                    </div>
                </div>
                <div className="m-kpi-card" style={{ flex: 1 }}>
                    <div className="m-kpi-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                        건당 평균 수리 비용
                    </div>
                    <div className="m-kpi-value" style={{ color: '#047857' }}>
                        {filteredRecords.length ? Math.round(totalCostThisMonth / filteredRecords.length).toLocaleString() : 0} <span className="m-kpi-sub" style={{ color: '#34d399' }}>원</span>
                    </div>
                </div>
                <div className="m-kpi-card" style={{ flex: 1 }}>
                    <div className="m-kpi-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        평균 수리 소요 일수
                    </div>
                    <div className="m-kpi-value" style={{ color: '#b45309' }}>
                        {avgDowntime} <span className="m-kpi-sub" style={{ color: '#fbbf24' }}>일 (가동 불가)</span>
                    </div>
                </div>
            </div>

            {/* 통합 툴바 */}
            <div className="driving-toolbar" style={{ marginTop: '0', border: '1px solid #e2e8f0', borderBottom: 'none', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                <div className="driving-toolbar-left" style={{ gap: '16px' }}>
                    <div className="driving-section-title">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"></polyline>
                            <line x1="13" y1="19" x2="19" y2="13"></line>
                            <line x1="16" y1="16" x2="20" y2="20"></line>
                            <line x1="19" y1="21" x2="21" y2="19"></line>
                        </svg>
                        정비 목록
                        <button className="btn-refresh" title="새로고침" onClick={() => window.location.reload()}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="23 4 23 10 17 10"></polyline>
                                <polyline points="1 20 1 14 7 14"></polyline>
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                            </svg>
                        </button>
                    </div>
                    <div className="driving-tabs">
                        <button className={`driving-tab-btn ${quickFilter === "all" ? "active" : ""}`} onClick={() => setQuickFilter("all")}>전체</button>
                        <button className={`driving-tab-btn ${quickFilter === "사고 수리" ? "active" : ""}`} onClick={() => setQuickFilter("사고 수리")}>사고 수리</button>
                        <button className={`driving-tab-btn ${quickFilter === "일반 정비" ? "active" : ""}`} onClick={() => setQuickFilter("일반 정비")}>일반 정비</button>
                        <button className={`driving-tab-btn ${quickFilter === "정기 검사" ? "active" : ""}`} onClick={() => setQuickFilter("정기 검사")}>정기 검사</button>
                    </div>
                </div>
                <div className="driving-toolbar-right">
                    <button className="driving-action-btn primary-outline" onClick={() => alert("API 수동 동기화 요청 큐에 추가되었습니다.")} style={{ background: 'white' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10"></polyline>
                            <polyline points="1 20 1 14 7 14"></polyline>
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                        </svg>
                        동기화
                    </button>
                    <button className="driving-action-btn green-solid">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <line x1="8" y1="13" x2="16" y2="13"></line>
                            <line x1="8" y1="17" x2="16" y2="17"></line>
                            <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        데이터 내보내기
                    </button>
                    <div className="driving-search-wrapper">
                        <input
                            type="text"
                            placeholder="차량번호, 운전자 검색"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="driving-search-input"
                        />
                        <svg className="driving-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                    </div>
                </div>
            </div>

            {/* Grid Area */}
            <div className="vm-main">
                <div className="soh-table-card">
                    <div className="soh-table-wrapper" style={{ overflowX: 'auto' }}>
                        <table className="premium-grid vehicle-grid" style={{ minWidth: '950px' }}>
                            <thead>
                                <tr>
                                    <th className="w-chk">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.length === filteredRecords.length && filteredRecords.length > 0}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <th>발생 일자</th>
                                    <th>차량</th>
                                    <th style={{ backgroundColor: '#f0f9ff' }}>당시 운전자</th>
                                    <th>누적 주행거리</th>
                                    <th>구분 (과실)</th>
                                    <th>정비소 / 수리내역</th>
                                    <th style={{ textAlign: 'right' }}>비용 (원)</th>
                                    <th>상태</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRecords.map((r) => (
                                    <tr
                                        key={r.id}
                                        className={selectedRecord?.id === r.id ? "soh-row-selected" : ""}
                                        onClick={() => setSelectedRecord(r)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td className="w-chk" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(r.id)}
                                                onChange={() => handleSelectOne(r.id)}
                                            />
                                        </td>
                                        <td>{r.date}</td>
                                        <td className="text-left">
                                            <div className="grid-icon-text">

                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginLeft: '4px' }}>
                                                    <span style={{ color: '#0f172a', fontWeight: 600 }}>
                                                        {r.vehicleNumber}
                                                        <span className="grid-badge" style={{ background: '#e0f2fe', color: '#0284c7', fontSize: '11px', padding: '1px 4px', borderRadius: '3px', marginLeft: '6px', fontWeight: 600 }}>{r.depot}</span>
                                                    </span>
                                                    <span style={{ color: '#64748b', fontSize: '12px' }}>{r.vehicleModel}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ backgroundColor: '#f0f9ff' }}>
                                            <div className="grid-icon-text" style={{ display: 'flex', justifyContent: 'center' }}>
                                                <div className="grid-avatar" style={{ background: '#bae6fd', color: '#0369a1' }}>{r.driver.charAt(0)}</div>
                                                <span style={{ fontWeight: 600, color: '#0369a1' }}>{r.driver}</span>
                                            </div>
                                        </td>
                                        <td style={{ color: '#475569' }}>
                                            {r.accumulatedMileage.toLocaleString()} km
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                                                <div className={`grid-badge ${getCategoryBadgeClass(r.category)}`}>
                                                    {r.category}
                                                </div>
                                                {r.faultLevel && (
                                                    <span className={`liability-badge ${r.faultLevel.includes('운전자') ? 'fault-driver' : 'fault-none'}`}>
                                                        {r.faultLevel}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-left">
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <span style={{ color: '#334155', fontWeight: 600 }}>{r.shop}</span>
                                                <span style={{ color: '#64748b', fontSize: '13px' }}>{r.details}</span>
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>
                                            {r.cost.toLocaleString()}
                                        </td>
                                        <td>
                                            {r.status === "완료" ? (
                                                <div className="grid-badge grid-badge-neutral">수리 완료</div>
                                            ) : r.status === "진행중" ? (
                                                <div className="grid-badge" style={{ background: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe' }}>진행 중</div>
                                            ) : (
                                                <div className="grid-badge" style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>예약됨</div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {filteredRecords.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="soh-empty">검색 결과가 없습니다.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Right Panel Drawer */}
                <div className={`vm-backdrop ${isDrawerOpen ? "open" : ""}`} onClick={() => setSelectedRecord(null)}></div>
                <aside className={`vm-form-container ${isDrawerOpen ? "open" : ""}`}>
                    {selectedRecord && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div>
                                    <h3 className="vm-form-title" style={{ margin: 0 }}>정비 상세 이력</h3>
                                </div>
                                <button onClick={() => setSelectedRecord(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>
                                    &times;
                                </button>
                            </div>

                            {selectedRecord.apiSynced && (
                                <div className="m-drawer-api-sync">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                    </svg>
                                    국토교통부 자동차종합정보망 연동 확인됨
                                </div>
                            )}

                            <div className="vm-form-body" style={{ marginTop: '16px' }}>
                                <div className="m-drawer-section">
                                    <div className="m-drawer-label">당시 운행 책임자</div>
                                    <div className="grid-icon-text" style={{ marginTop: '8px' }}>
                                        <div className="grid-avatar" style={{ width: '36px', height: '36px', fontSize: '14px' }}>{selectedRecord.driver.charAt(0)}</div>
                                        <div>
                                            <div className="m-drawer-value">{selectedRecord.driver}</div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>배차 기간 중 사고 발생</div>
                                        </div>
                                    </div>
                                </div>

                                {selectedRecord.category === "사고 수리" && (
                                    <div className="m-drawer-section" style={{ background: '#fff1f2', borderColor: '#ffe4e6', position: 'relative', overflow: 'hidden' }}>
                                        <div style={{ position: 'absolute', right: '-10px', top: '-10px', opacity: 0.1 }}>
                                            <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="#e11d48" strokeWidth="2">
                                                <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
                                            </svg>
                                        </div>
                                        <div className="m-drawer-label" style={{ color: '#e11d48', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                                <line x1="12" y1="9" x2="12" y2="13"></line>
                                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                            </svg>
                                            중고차 시세 감가 영향도
                                        </div>
                                        <div className="m-drawer-value" style={{ color: '#9f1239', fontSize: '14px' }}>
                                            {selectedRecord.details.includes("프레임") ? "주요 골격 손상 확인 - 예상 시세 15% 하락" : "단순 외관 수리 - 감가 영향 적음"}
                                        </div>
                                    </div>
                                )}

                                <div className="m-drawer-section">
                                    <div className="m-drawer-label" style={{ marginBottom: '12px' }}>정비 명세서 요약</div>
                                    <div className="m-cost-row">
                                        <div className="m-cost-label">발생 일자</div>
                                        <div className="m-cost-val">{selectedRecord.date}</div>
                                    </div>
                                    <div className="m-cost-row">
                                        <div className="m-cost-label">누적 주행거리</div>
                                        <div className="m-cost-val">{selectedRecord.accumulatedMileage.toLocaleString()} km</div>
                                    </div>
                                    <div className="m-cost-row">
                                        <div className="m-cost-label">수리 내역</div>
                                        <div className="m-cost-val" style={{ textAlign: 'right', maxWidth: '60%' }}>{selectedRecord.details}</div>
                                    </div>
                                    {(selectedRecord.laborCost || selectedRecord.partsCost) && (
                                        <div style={{ background: '#f1f5f9', padding: '8px 12px', borderRadius: '6px', marginTop: '12px', fontSize: '13px' }}>
                                            {selectedRecord.laborCost && (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                    <span style={{ color: '#64748b' }}>공임비</span>
                                                    <span style={{ fontWeight: 600 }}>{selectedRecord.laborCost.toLocaleString()} 원</span>
                                                </div>
                                            )}
                                            {selectedRecord.partsCost && (
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span style={{ color: '#64748b' }}>부품대</span>
                                                    <span style={{ fontWeight: 600 }}>{selectedRecord.partsCost.toLocaleString()} 원</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="m-cost-row" style={{ marginTop: '8px', paddingTop: '12px', borderTop: '1px solid #cbd5e1' }}>
                                        <div className="m-cost-label" style={{ fontWeight: 600 }}>총 결제 금액 (VAT 포함)</div>
                                        <div className="m-cost-val" style={{ fontSize: '18px', color: '#2563eb' }}>{selectedRecord.cost.toLocaleString()} 원</div>
                                    </div>
                                </div>

                                <div className="m-drawer-section">
                                    <div className="m-drawer-label">정비소 정보 및 특이사항</div>
                                    <div className="m-drawer-value" style={{ marginBottom: '4px' }}>{selectedRecord.shop}</div>
                                    <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                                        {selectedRecord.technicianNote || "등록된 노트가 없습니다."}
                                    </div>
                                </div>

                                {selectedRecord.nextServiceDate && (
                                    <div className="m-drawer-section" style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
                                        <div className="m-drawer-label" style={{ color: '#b45309' }}>다음 점검 권장일</div>
                                        <div className="m-drawer-value" style={{ color: '#92400e' }}>
                                            {selectedRecord.nextServiceDate}
                                            <span style={{ fontSize: '12px', marginLeft: '8px', fontWeight: 500 }}>예정됨</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="vm-form-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '16px 24px', position: 'absolute', bottom: 0, left: 0, right: 0, background: 'white', display: 'flex', gap: '8px' }}>
                                <button className="vm-btn-cancel" style={{ flex: 1 }} onClick={() => setSelectedRecord(null)}>닫기</button>
                                <button className="vm-action-btn-compact primary" style={{ flex: 1, padding: '10px' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    명세서 다운로드
                                </button>
                            </div>
                        </>
                    )}
                </aside>
            </div>

            {/* 수동 등록 슬라이딩 패널 (Drawer) */}
            <div className={`vm-backdrop ${isManualAddOpen ? "open" : ""}`} onClick={() => setIsManualAddOpen(false)}></div>
            <aside className={`vm-form-container ${isManualAddOpen ? "open" : ""}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '0 4px' }}>
                    <div>
                        <h3 className="vm-form-title" style={{ margin: 0 }}>정비 내역 수동 등록</h3>
                    </div>
                    <button onClick={() => setIsManualAddOpen(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>
                        &times;
                    </button>
                </div>

                <div className="vm-form-body">
                    <div className="al-info-box" style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontWeight: 600, color: '#334155', marginBottom: '4px' }}>수동 등록 안내</div>
                        <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                            선택하신 정비 구분에 따라 필요한 필수 입력 항목이 달라집니다. 정확한 이력 관리를 위해 모든 항목을 꼼꼼히 기입해 주세요.
                        </div>
                    </div>
                    
                    <div className="m-drawer-section">
                        <div className="al-form-group" style={{ marginBottom: '16px' }}>
                            <label className="al-form-label" style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>정비 구분 <span style={{ color: '#ef4444' }}>*</span></label>
                            <select 
                                className="al-form-input" 
                                value={manualAddCategory}
                                onChange={(e) => setManualAddCategory(e.target.value as MaintenanceCategory)}
                                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: 600, color: '#0f172a' }}
                            >
                                <option value="일반 정비">일반 정비 (소모품/고장 등)</option>
                                <option value="사고 수리">사고 수리</option>
                                <option value="정기 검사">정기 검사</option>
                            </select>
                        </div>

                        <div className="al-form-group" style={{ marginBottom: '16px' }}>
                            <label className="al-form-label" style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>차량 선택 <span style={{ color: '#ef4444' }}>*</span></label>
                            <select className="al-form-input" style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}>
                                <option value="">차량을 선택해주세요</option>
                                <option value="12가1111">12가 1111 (Kia Bongo III EV)</option>
                                <option value="34나2222">34나 2222 (Hyundai Porter II Electric)</option>
                                <option value="56다3333">56다 3333 (Hyundai ST1)</option>
                            </select>
                        </div>
                        
                        <div className="al-form-group" style={{ marginBottom: '16px' }}>
                            <label className="al-form-label" style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>발생(완료) 일자 <span style={{ color: '#ef4444' }}>*</span></label>
                            <input type="date" className="al-form-input" style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }} defaultValue={new Date().toISOString().split('T')[0]} />
                        </div>
                    </div>

                    <div className="m-drawer-section">
                        {/* 조건부 렌더링 영역 */}
                        {manualAddCategory === "일반 정비" && (
                            <>
                                <div className="m-drawer-label" style={{ marginBottom: '12px' }}>일반 정비 상세 상세</div>
                                <div className="al-form-group" style={{ marginBottom: '12px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>정비 항목 (다중 선택 가능)</label>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" /> 타이어 교체</label>
                                        <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" /> 브레이크 패드</label>
                                        <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" /> 와이퍼/워셔액</label>
                                        <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" /> 기타</label>
                                    </div>
                                </div>
                                <div className="al-form-group" style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>상세 내역 입력</label>
                                    <textarea className="al-form-input" placeholder="정비 상세 내용 입력" style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', minHeight: '60px', resize: 'vertical' }}></textarea>
                                </div>
                            </>
                        )}

                        {manualAddCategory === "사고 수리" && (
                            <>
                                <div className="m-drawer-label" style={{ color: '#e11d48', marginBottom: '12px' }}>사고 수리 상세 보고</div>
                                <div className="al-form-group" style={{ marginBottom: '12px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>과실 비율 <span style={{ color: '#ef4444' }}>*</span></label>
                                    <select className="al-form-input" style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}>
                                        <option value="">과실 비율 선택</option>
                                        <option value="운전자 100%">우리측(운전자) 100%</option>
                                        <option value="쌍방 과실">쌍방 과실</option>
                                        <option value="상대방 100%">상대측 100%</option>
                                    </select>
                                </div>
                                <div className="al-form-group" style={{ marginBottom: '12px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>사고 부위 (파손 부위)</label>
                                    <input type="text" className="al-form-input" placeholder="예: 전면 범퍼, 우측 휀더 등" style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
                                </div>
                                <div className="al-form-group" style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>수리 세부 내역 (판금/도색 등)</label>
                                    <textarea className="al-form-input" placeholder="상세 수리 방법 입력" style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', minHeight: '60px', resize: 'vertical' }}></textarea>
                                </div>
                            </>
                        )}

                        {manualAddCategory === "정기 검사" && (
                            <>
                                <div className="m-drawer-label" style={{ color: '#0369a1', marginBottom: '12px' }}>정기 검사 결과 보고</div>
                                <div className="al-form-group" style={{ marginBottom: '12px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>검사 여부 및 결과 <span style={{ color: '#ef4444' }}>*</span></label>
                                    <select className="al-form-input" style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}>
                                        <option value="적합">적합 (합격)</option>
                                        <option value="시정권고">시정 권고</option>
                                        <option value="부적합">부적합 (재검 필요)</option>
                                    </select>
                                </div>
                                <div className="al-form-group" style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>다음 정기 검사 권장일</label>
                                    <input type="date" className="al-form-input" style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="m-drawer-section">
                        <div className="m-drawer-label" style={{ marginBottom: '12px' }}>비용 및 증빙 내역</div>
                        <div className="al-form-group" style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>정비소 및 업체명 <span style={{ color: '#ef4444' }}>*</span></label>
                            <input type="text" className="al-form-input" placeholder="정비소/검사소 명칭" style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
                        </div>
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                            <div className="al-form-group" style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>공임비 (원)</label>
                                <input type="number" className="al-form-input" placeholder="0" style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', textAlign: 'right' }} />
                            </div>
                            <div className="al-form-group" style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>부품대 (원)</label>
                                <input type="number" className="al-form-input" placeholder="0" style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', textAlign: 'right' }} />
                            </div>
                        </div>
                        <div className="al-form-group" style={{ marginBottom: '8px' }}>
                            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>총 결제 비용 (원) <span style={{ color: '#ef4444' }}>*</span></label>
                            <input type="number" className="al-form-input" placeholder="0" style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '2px solid #e2e8f0', fontSize: '16px', fontWeight: 700, textAlign: 'right', color: '#0f172a' }} />
                        </div>
                    </div>
                </div>

                <div className="vm-form-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '16px 24px', position: 'absolute', bottom: 0, left: 0, right: 0, background: 'white', display: 'flex', gap: '8px' }}>
                    <button className="vm-btn-cancel" style={{ flex: 1 }} onClick={() => setIsManualAddOpen(false)}>취소</button>
                    <button className="vm-action-btn-compact primary" style={{ flex: 2, padding: '10px' }} onClick={() => { setIsManualAddOpen(false); alert("해당 정비 내역이 수동으로 성공적으로 등록되었습니다."); }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                            <polyline points="17 21 17 13 7 13 7 21"></polyline>
                            <polyline points="7 3 7 8 15 8"></polyline>
                        </svg>
                        정비 내역 저장
                    </button>
                </div>
            </aside>
        </div>
    );
}
